// Copyright (c) Ping. All rights reserved.
//
// Collaborative annotation overlay. Renders every participant's strokes (synced
// over the data channel, normalized 0..1 so they map across viewers) and, in
// draw mode, captures local strokes.
//
// REALTIME + PERFORMANCE. Repaints are driven by React state (the `liveStrokes`
// selector + the `strokes` prop) — the proven path for remote strokes to show
// live — but the cost is kept flat by caching committed strokes on an OFFSCREEN
// canvas that is rebuilt ONLY when `strokes` change. A repaint then = one
// `drawImage` blit of that cache + the handful of in-progress strokes, so it
// never re-strokes history per frame (that O(n²) blowup is what froze Safari).
// The live broadcast is throttled + INCREMENTAL (only new points since the last
// send; the receiver appends), so the wire + receiver cost is O(n) total.

import { useEffect, useRef } from 'react';

import type { DrawStroke } from '@calls/livekit/callData';
import { useCallStore } from '@calls/store/callStore';

// Cap the live-broadcast rate. The final committed `draw` frame carries the full
// stroke regardless, so dropped intermediate frames only soften the live preview
// for remotes — never lose data. ~30fps is smooth and stays cheap on the wire.
const LIVE_BROADCAST_MS = 33;

type Pt = { color: string; width: number; points: number[] };

function strokePath(ctx: CanvasRenderingContext2D, s: Pt, w: number, h: number): void {
  if (s.points.length < 4) return;
  ctx.strokeStyle = s.color;
  ctx.lineWidth = s.width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(s.points[0]! * w, s.points[1]! * h);
  for (let i = 2; i < s.points.length; i += 2) ctx.lineTo(s.points[i]! * w, s.points[i + 1]! * h);
  ctx.stroke();
}

export function DrawingCanvas({
  drawMode,
  strokes,
}: {
  drawMode: boolean;
  strokes: readonly DrawStroke[];
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Offscreen cache of committed strokes (rebuilt only on strokes/size change).
  const cacheRef = useRef<HTMLCanvasElement | null>(null);
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 });
  const strokesRef = useRef(strokes);
  strokesRef.current = strokes;
  // Local in-progress stroke (full point list, kept locally for the commit).
  const liveRef = useRef<number[] | null>(null);
  const liveIdRef = useRef<string | null>(null);
  // Broadcast bookkeeping: how many points we've already sent + when.
  const sentLenRef = useRef(0);
  const sentAtRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  // Subscribing here is what makes REMOTE live strokes repaint in realtime: a
  // remote `draw_live` frame → store `set({liveStrokes})` → re-render → repaint.
  const liveStrokes = useCallStore((s) => s.liveStrokes);

  // Rebuild the committed-strokes cache (offscreen). O(strokes), but only runs on
  // a commit / resize, never per animation frame.
  const rebuildCache = (): void => {
    const { w, h, dpr } = sizeRef.current;
    if (w === 0 || h === 0) return;
    let cache = cacheRef.current;
    if (!cache) {
      cache = document.createElement('canvas');
      cacheRef.current = cache;
    }
    cache.width = Math.max(1, Math.round(w * dpr));
    cache.height = Math.max(1, Math.round(h * dpr));
    const ctx = cache.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    for (const s of strokesRef.current) strokePath(ctx, s, w, h);
  };

  // Composite the visible canvas: cached committed strokes (one blit) + the
  // in-progress strokes (remote live + our own). Reads live state fresh so a
  // coalesced rAF can never paint a stale snapshot.
  const redraw = (): void => {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    const { w, h } = sizeRef.current;
    ctx.clearRect(0, 0, w, h);
    if (cacheRef.current) ctx.drawImage(cacheRef.current, 0, 0, w, h);
    const { liveStrokes: ls, drawColor } = useCallStore.getState();
    for (const s of Object.values(ls)) {
      if (s.id !== liveIdRef.current) strokePath(ctx, s, w, h);
    }
    if (liveRef.current) {
      strokePath(ctx, { color: drawColor, width: 3, points: liveRef.current }, w, h);
    }
  };

  const scheduleRedraw = (): void => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      redraw();
    });
  };

  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  // Keep the canvas sized to its box (crisp via devicePixelRatio); rebuild the
  // cache + repaint on resize.
  useEffect(() => {
    const c = canvasRef.current;
    const parent = c?.parentElement;
    if (!c || !parent) return;
    const ro = new ResizeObserver(() => {
      const r = parent.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      sizeRef.current = { w: r.width, h: r.height, dpr };
      c.width = Math.max(1, Math.round(r.width * dpr));
      c.height = Math.max(1, Math.round(r.height * dpr));
      c.style.width = `${r.width}px`;
      c.style.height = `${r.height}px`;
      const ctx = c.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      rebuildCache();
      redraw();
    });
    ro.observe(parent);
    return () => ro.disconnect();
     
  }, []);

  // Committed strokes changed → rebuild the cache + repaint.
  useEffect(() => {
    rebuildCache();
    redraw();
     
  }, [strokes]);

  // Remote live strokes changed → repaint (cheap: cache blit + live strokes).
  useEffect(() => {
    redraw();
     
  }, [liveStrokes]);

  const toNorm = (e: React.PointerEvent): [number, number] => {
    const r = canvasRef.current!.getBoundingClientRect();
    return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
  };

  const onDown = (e: React.PointerEvent): void => {
    if (!drawMode) return;
    canvasRef.current?.setPointerCapture(e.pointerId);
    const [x, y] = toNorm(e);
    liveRef.current = [x, y];
    sentLenRef.current = 0;
    sentAtRef.current = 0;
    // Stable id shared by every live frame AND the final stroke, so the remote
    // can supersede the live preview with the committed stroke (same id).
    liveIdRef.current = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  };

  const onMove = (e: React.PointerEvent): void => {
    if (!drawMode || !liveRef.current) return;
    const [x, y] = toNorm(e);
    liveRef.current.push(x, y);
    scheduleRedraw();
    // Throttled, incremental broadcast — send only the points added since the
    // last send so it's O(delta) per frame and O(n) per stroke, not O(n²).
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - sentAtRef.current >= LIVE_BROADCAST_MS) {
      const seg = liveRef.current.slice(sentLenRef.current);
      if (seg.length >= 2 && liveIdRef.current) {
        sentLenRef.current = liveRef.current.length;
        sentAtRef.current = now;
        useCallStore.getState().sendDrawLive(liveIdRef.current, seg);
      }
    }
  };

  const onUp = (): void => {
    if (!liveRef.current) return;
    const pts = liveRef.current;
    const id = liveIdRef.current;
    // Flush the tail so the remote's live preview reaches pointer-up before the
    // committed `draw` frame supersedes it (no visible snap).
    if (id && pts.length > sentLenRef.current) {
      const seg = pts.slice(sentLenRef.current);
      if (seg.length >= 2) useCallStore.getState().sendDrawLive(id, seg);
    }
    liveRef.current = null;
    liveIdRef.current = null;
    redraw(); // clear the in-progress preview; the commit lands via the strokes effect
    if (pts.length >= 4) void useCallStore.getState().addStroke(pts, id ?? undefined);
  };

  return (
    <canvas
      ref={canvasRef}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 4,
        pointerEvents: drawMode ? 'auto' : 'none',
        cursor: drawMode ? 'crosshair' : 'default',
        touchAction: 'none',
      }}
    />
  );
}
